import React, { useState, useEffect } from "react";
import {
  Form, Input, Select, InputNumber, DatePicker,
  Row, Col, Spin, message, Upload, Button, Checkbox, Tooltip,
} from "antd";
import { PaperClipOutlined, SearchOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../services/api";

const { Option } = Select;

// ── Helper FireDAC robusto ───────────────────────────────────────────────
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const mgr    = data.FDBS.Manager ?? data.FDBS.manager;
      const tables = mgr?.TableList ?? mgr?.tablelist ?? [];
      if (tables.length > 0) {
        const rowList = tables[0].RowList ?? tables[0].rowlist ?? [];
        if (rowList.length > 0)
          return rowList.map((r) => r.Original ?? r.Current ?? r);
      }
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

const normalize = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

// ── Serialização para o Delphi ───────────────────────────────────────────
// StrToCurr no Delphi (locale pt-BR) exige vírgula decimal: "1234,56"
const toDelphiCurr = (v) => {
  const n = Number(v) || 0;
  return n.toFixed(2).replace(".", ",");
};

// StrToInt no Delphi não aceita pontos de milhar ("9.813.000" EXPLODE)
const toDelphiInt = (v) => String(Math.round(Number(v) || 0));

// Para valores fracionários "por milha" (ex: 0,018) que o backend MULTIPLICA por 1000.
// ⚠️ NÃO use toDelphiCurr aqui: ele arredonda p/ 2 casas e DESTRÓI o valor
//    (0,018 → "0,02"; 0,004 → "0,00" = ZERO). Precisamos de casas suficientes.
const toDelphiFloat = (v, casas = 6) => {
  const n = Number(v) || 0;
  return n.toFixed(casas).replace(".", ",");
};

// Formato de data aceito pelo StrToDateTime do backend (locale pt-BR do servidor).
// ⚠️ DÍVIDA TÉCNICA CONHECIDA: isso depende do locale da máquina que roda o
// serviço Delphi. Se o serviço subir numa máquina en-US, TODAS as datas quebram.
// O certo é migrar para ISO (YYYY-MM-DD HH:mm:ss) e parsear no Delphi com um
// TFormatSettings explícito. Enquanto isso não acontece, manter DD/MM/YYYY.
const DELPHI_DT = "DD/MM/YYYY HH:mm:ss";

// Tipo da operação (campo TipoOperacao), derivado da origem das milhas.
// ⚠️ CONFIRA estas strings: são persistidas e usadas em GerarTransacao no backend.
const TIPO_OPERACAO = {
  proprias:      "FRENTE VOO",
  balcao:        "BALCÃO DE MILHAS",
  consolidadora: "CONSOLIDADORA",
};

// ── Input de valor monetário (padrão BR) — máscara por dígitos ──────────
//
// POR QUE NÃO usar InputNumber com formatter/parser aqui:
// O InputNumber do AntD, quando controlado (value vindo do state do pai),
// reconstrói o texto exibido a partir do NÚMERO já convertido a cada
// keystroke. Assim que o usuário digita a vírgula decimal, o parser
// converte a string parcial ("18.521,") num float que descarta a vírgula
// sem dígito depois dela (parseFloat("18521.") = 18521), o state volta a
// ser um inteiro puro, e o formatter reformata a partir desse inteiro —
// apagando a vírgula que o usuário acabou de digitar. O campo nunca deixa
// completar os centavos e o valor final sai errado.
//
// A solução robusta é tratar o campo como máscara de dígitos: o usuário
// digita dígitos da direita pra esquerda, os 2 últimos são sempre
// centavos. Não existe vírgula, ponto ou parser ambíguo envolvido — o
// número é derivado diretamente da sequência de dígitos digitada, então
// não há estado intermediário que se perca.
const MoneyInput = ({ value, onChange, style, placeholder = "0,00", suffix }) => (
  <Input
    size="middle"
    style={style}
    placeholder={placeholder}
    value={
      !value
        ? ""
        : Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
    onChange={(e) => {
      const raw = e.target.value.replace(/\D/g, "");
      if (raw === "") { onChange(0); return; }
      onChange(parseInt(raw, 10) / 100);
    }}
    suffix={suffix}
  />
);

// Versão para campos de quantidade inteira (milhas) — sem decimais
const fmtMilhas = (v) => {
  if (v === "" || v === undefined || v === null) return "";
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};
const parseMilhas = (v) => {
  if (!v) return 0;
  const n = parseInt(String(v).replace(/\./g, ""), 10);
  return isNaN(n) ? 0 : n;
};

// ── Logos programas com cores exatas e gradientes ─────────────────────
const LOGOS_MAP = [
  // ── Nacionais ──────────────────────────────────────────────────────
  { keys: ["livelo"],                    bg: "#e8008b", letra: "LV"  },
  { keys: ["itaú pontos","itau pontos"], bg: "#d48c0c", letra: "IT"  },
  { keys: ["smiles"],                    bg: "#ff6600", letra: "S"   },
  { keys: ["azul fidelidade","azul"],    bg: "#003087", letra: "A"   },
  { keys: ["coopera pj"],                bg: "#007b5e", letra: "CPJ" },
  { keys: ["coopera"],                   bg: "#007b5e", letra: "COO" },
  { keys: ["latam"],                     bg: "#d81a36", letra: "L"   },
  { keys: ["tap miles","tap"],           bg: "#009900", letra: "TAP" },
  { keys: ["max milhas"],                bg: "#1e3a6e", letra: "MM"  },
  { keys: ["hotmilhas"],                 bg: "#cc3300", letra: "HM"  },
  { keys: ["ipiranga"],                  bg: "#f5a800", letra: "IP"  },
  { keys: ["esfera empresas"],           bg: "#8b0000", letra: "EE"  },
  { keys: ["esfera"],                    bg: "#8b0000", letra: "E_"  },
  { keys: ["bank milhas"],               bg: "#0066cc", letra: "BM"  },
  { keys: ["cash milhas"],               bg: "#5a5a5a", letra: "CM"  },
  { keys: ["curtaí","curtai"],           bg: "#e8a000", letra: "CT"  },
  { keys: ["compro suas milhas"],        bg: "#1e3a4a", letra: "CSM" },
  { keys: ["átomos","atomos"],           bg: "#000000", letra: "ÁT"  },
  { keys: ["gpa"],                       bg: "#00aa44", letra: "GPA" },
  { keys: ["pontos caixa"],              bg: "#007b5e", letra: "PC"  },
  { keys: ["caixa empresas"],            bg: "#007b5e", letra: "CE"  },
  { keys: ["sicredi empresarial"],       bg: "#009900", letra: "SCE" },
  { keys: ["sicredi"],                   bg: "#009900", letra: "SC"  },
  { keys: ["dotz"],                      bg: "#ff6600", letra: "DZ"  },
  { keys: ["porto plus"],                bg: "#0057a8", letra: "PP"  },
  { keys: ["inter loop"],                bg: "#ff6b00", letra: "IL"  },
  { keys: ["inter empresas"],            bg: "#ff6b00", letra: "IE"  },
  { keys: ["volare"],                    bg: "#c8102e", letra: "VL"  },
  { keys: ["membership rewards","amex"], bg: "#006fcf", letra: "MR"  },
  { keys: ["safra rewards"],             bg: "#003366", letra: "SR"  },
  { keys: ["suma"],                      bg: "#1e3a4a", letra: "SU"  },
  { keys: ["cressol"],                   bg: "#007b5e", letra: "CR"  },
  { keys: ["banrisul"],                  bg: "#003087", letra: "BR"  },
  { keys: ["unicred"],                   bg: "#009900", letra: "UC"  },
  { keys: ["milhas plus"],               bg: "#e8a000", letra: "M+"  },
  { keys: ["xp pontos"],                 bg: "#1a1a2e", letra: "XP"  },
  { keys: ["nubank"],                    bg: "#820ad1", letra: "NU"  },
  { keys: ["ailos"],                     bg: "#007b5e", letra: "AI"  },
  { keys: ["banestes"],                  bg: "#003087", letra: "BN"  },
  { keys: ["bb empresas"],               bg: "#f5a800", letra: "BB"  },
  { keys: ["itaú empresas","itau empresas"], bg: "#d48c0c", letra: "ITE" },
  { keys: ["pontos btg"],                bg: "#1a1a2e", letra: "BTG" },

  // ── Internacionais ──────────────────────────────────────────────────
  { keys: ["iberia"],                    bg: "#cc0000", letra: "IB"  },
  { keys: ["mileageplus","united"],      bg: "#002244", letra: "UA"  },
  { keys: ["all accor","accor"],         bg: "#6a628a", letra: "AL"  },
  { keys: ["Qantas","qantas"],           bg: "#c33333", letra: "QT"},
  { keys: ["ihg"],                       bg: "#6a0dad", letra: "IHG" },
  { keys: ["aadvantage","american"],     bg: "#0078d2", letra: "AA"  },
  { keys: ["flying blue"],               bg: "#003087", letra: "FB"  },
  { keys: ["british airways"],           bg: "#2b4090", letra: "BA"  },
  { keys: ["qatar"],                     bg: "#5c0632", letra: "QR"  },
  { keys: ["aeroplan"],                  bg: "#c8102e", letra: "AP"  },
  { keys: ["copa","connectmiles"],       bg: "#0033a0", letra: "CM"  },
  { keys: ["delta"],                     bg: "#c8102e", letra: "DL"  },
  { keys: ["emirates"],                  bg: "#c8102e", letra: "EK"  },
  { keys: ["virgin atlantic","flying club"], bg: "#e8003d", letra: "VS" },
  { keys: ["finnair"],                   bg: "#003580", letra: "AY"  },
  { keys: ["wyndham"],                   bg: "#003087", letra: "WY"  },
];

const getLogo = (nome = "") => {
  const n = nome.toLowerCase();
  for (const l of LOGOS_MAP) {
    if (l.keys.some((k) => n.includes(k))) return l;
  }
  return { bg: "#607d8b", letra: (nome || "??").slice(0, 2).toUpperCase() };
};

const LogoCircle = ({ nome, size = 20 }) => {
  const { bg, letra } = getLogo(nome);
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%",
      background: bg, color: "#fff",
      fontSize: size * 0.33, fontWeight: 700, flexShrink: 0,
    }}>{letra}</span>
  );
};

export default function EmissaoUsoProprioVenda({ clienteId: clienteIdProp }) {
  const [form] = Form.useForm();
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [cartoes, setCartoes]         = useState([]);
  const [passageiros, setPassageiros] = useState([]);
  const [titulares, setTitulares]     = useState([]);
  const [clienteId, setClienteId]     = useState(null);

  const [origemMilhas, setOrigemMilhas]               = useState("proprias");
  const [nomeFornecedor, setNomeFornecedor]           = useState("");
  const [contatoFornecedor, setContatoFornecedor]     = useState("");
  const [custoMilheiroBalcao, setCustoMilheiroBalcao] = useState(0);
  const [nomeConsolidadora, setNomeConsolidadora]     = useState("");
  const [precoConsolidadora, setPrecoConsolidadora]   = useState(0);

  const [cartaoSel, setCartaoSel]       = useState(null);
  const [precoPagante, setPrecoPagante] = useState(0);
  const [milhasUtil, setMilhasUtil]     = useState(0);
  const [taxas, setTaxas]               = useState(0);
  const [bagagem, setBagagem]           = useState(0);
  const [fileList, setFileList]         = useState([]);
  const [passagSel, setPassagSel]       = useState([]);
  const [buscaPassag, setBuscaPassag]   = useState("");
  const [interline, setInterline]       = useState(false);

  // 🔥 NOVO: voo de ida e volta. Controla a exibição do DatePicker de retorno
  // e decide se DataVooVolta vai preenchida ou vazia no payload.
  // Quando false, o backend grava NULL em ven_data_voo_volta.
  const [idaVolta, setIdaVolta]         = useState(false);

  // custoMedio = R$ por MILHEIRO (ex: 18,00 = R$18 por 1.000 milhas)
  const custoMedio = cartaoSel ? Number(cartaoSel.car_vunitario ?? 0) : 0;
  const saldoDisp  = cartaoSel ? Number(cartaoSel.car_saldo_milhas ?? 0) : 0;
  const cpfsLivres = cartaoSel ? Number(cartaoSel.car_qtde_cpf ?? 1) : 0;

  // Interline (emissões internacionais Azul) NÃO consome cota de CPF do programa.
  const isAzul = (cartaoSel?.car_nome_programa || "").toLowerCase().includes("azul");

  // Consolidadora = venda de passagem de terceiros: não usa cota de CPF de
  // nenhum programa (não há cartão envolvido), então nunca contabiliza CPF,
  // igual ao Interline.
  const semControleCpf = interline || origemMilhas === "consolidadora";
  const cpfsContabilizados = semControleCpf ? 0 : passagSel.length;

  // custoCalc = custo do MILHEIRO para a operação atual
  const custoCalc = origemMilhas === "balcao" ? custoMilheiroBalcao : custoMedio;

  // precoEmMilhas = R$ real gasto nas milhas
  const precoEmMilhas = custoCalc > 0 && milhasUtil > 0
    ? parseFloat(((custoCalc / 1000) * milhasUtil).toFixed(2))
    : 0;

  const baseDesconto = origemMilhas === "consolidadora" ? precoConsolidadora : precoEmMilhas;

  // 🔥 CORRIGIDO: define se a operação tem os dados mínimos preenchidos,
  // INDEPENDENTE do valor do custo em si. Antes, o gate usava "baseDesconto > 0",
  // o que tratava custo = 0 (legítimo: milhas bonificadas, transferência gratuita,
  // cartão sem custo cadastrado de propósito) como se faltassem dados — e zerava
  // Economia e % inteiros mesmo com cartão selecionado e milhas informadas.
  const operacaoValida = origemMilhas === "consolidadora"
    ? precoConsolidadora > 0
    : (milhasUtil > 0 && !!cartaoSel);

  // ── CÁLCULO DE ECONOMIA (CORRIGIDO) ─────────────────────────────────────
  // Custo real da operação = custo das milhas (ou consolidadora) + taxas + bagagem.
  // Taxas e bagagem são desembolsos SEUS na emissão: entram no custo, NÃO no pagante.
  const custoTotalOperacao = parseFloat((baseDesconto + taxas + bagagem).toFixed(2));

  // Economia = preço da passagem pagante - custo total real da operação
  const economiaGerada = precoPagante > 0 && operacaoValida
    ? parseFloat((precoPagante - custoTotalOperacao).toFixed(2))
    : 0;

  // % de economia = economia / preço da passagem pagante (à vista) * 100
  // >>> Para exibir também operações negativas (prejuízo), troque a condição
  //     "economiaGerada > 0" por "economiaGerada !== 0".
  const economiaPct = precoPagante > 0 && operacaoValida && economiaGerada > 0
    ? parseFloat(((economiaGerada / precoPagante) * 100).toFixed(2))
    : 0;

  useEffect(() => {
    let id = clienteIdProp;
    if (!id) {
      try { const u = JSON.parse(localStorage.getItem("user") || "{}"); id = u.cli_id || u.id; } catch (e) {}
    }
    if (!id) id = parseInt(localStorage.getItem("userID") || "0") || null;
    if (id) { setClienteId(id); carregarDados(id); }
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  const carregarDados = async (id) => {
    setLoading(true);
    try {
      const titRes  = await api.get("/ServerPrincipal/PesquisaTitular", { params: { Cliente: id } });
      const titRows = extractRows(titRes.data).map(normalize);

      const seen = new Set();
      const titDeduplic = titRows.filter((t) => {
        const k = String(t.cli_id ?? "");
        if (!k || k === "0" || seen.has(k)) return false;
        seen.add(k); return true;
      });

      const titFinais = titDeduplic.length > 0
        ? titDeduplic
        : [{ cli_id: id, cli_nome: "Titular Principal" }];

      const carResArr = await Promise.all(
        titFinais.map((t) =>
          api.get("/ServerPrincipal/PesquisaCartoes", { params: { Cliente: t.cli_id } })
            .then((res) => ({ rows: extractRows(res.data).map(normalize), tit: t }))
            .catch(() => ({ rows: [], tit: t }))
        )
      );

      const cartoesFlat = [];
      const seenCar = new Set();
      carResArr.forEach(({ rows, tit }) => {
        rows.forEach((c) => {
          const carKey = String(c.car_id ?? "");
          if (!carKey || carKey === "0" || seenCar.has(carKey)) return;
          seenCar.add(carKey);
          cartoesFlat.push({
            car_id:            c.car_id ?? 0,
            car_nome_programa: c.car_nome_programa ?? "",
            car_saldo_milhas:  Number(c.car_saldo_milhas ?? 0),
            car_vunitario:     Number(c.car_vunitario    ?? 0),
            car_qtde_cpf:      Number(c.car_qtde_cpf     ?? 0),
            cli_id:            tit.cli_id,
            cli_nome:          tit.cli_nome ?? "",
          });
        });
      });

      const passRes  = await api.get("/ServerPrincipal/PesquisaPassageiros", { params: { Cliente: id } }).catch(() => ({ data: [] }));
      const passRows = extractRows(passRes.data).map(normalize);

      setTitulares(titFinais);
      setCartoes(cartoesFlat);
      setPassageiros(passRows);
    } catch (e) {
      message.error("Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  const handleCartaoChange = (id) => {
    const c = cartoes.find((x) => x.car_id === id);
    setCartaoSel(c || null);
    // Interline só existe para Azul; ao mudar de conta, desliga se não for Azul.
    const azul = (c?.car_nome_programa || "").toLowerCase().includes("azul");
    if (!azul) setInterline(false);
  };

  const handleOrigemChange = (v) => {
    setOrigemMilhas(v);
    setCartaoSel(null);
    setInterline(false);
    setNomeFornecedor(""); setContatoFornecedor(""); setCustoMilheiroBalcao(0);
    setNomeConsolidadora(""); setPrecoConsolidadora(0);
    form.setFieldValue("cartaoId", undefined);
  };

  // Liga/desliga ida e volta. Ao desligar, limpa a data para não sobrar
  // valor fantasma no form que pudesse vazar para o payload.
  const handleIdaVoltaChange = (checked) => {
    setIdaVolta(checked);
    if (!checked) form.setFieldValue("dataVooVolta", undefined);
  };

  const passageirosFiltrados = passageiros.filter((p) =>
    !buscaPassag || (p.pag_nome || "").toLowerCase().includes(buscaPassag.toLowerCase())
  );

  const adicionarPassageiro = (p) => {
    if (passagSel.find((x) => x.pag_id === p.pag_id)) return;
    const primeiroNomeTit = (titulares[0]?.cli_nome || "").toLowerCase().split(" ")[0];
    const isTitular = !!primeiroNomeTit && (p.pag_nome || "").toLowerCase().startsWith(primeiroNomeTit);
    setPassagSel([...passagSel, { ...p, _isTitular: isTitular }]);
    setBuscaPassag("");
  };

  const removerPassageiro = (id) => setPassagSel(passagSel.filter((p) => p.pag_id !== id));

  // ── SUBMIT ────────────────────────────────────────────────────────────
  const handleSubmit = async (values) => {
    if (!clienteId)                    { message.error("Cliente não identificado."); return; }
    if (!values.localizador)           { message.error("Localizador é obrigatório."); return; }

    // 🔥 NOVO: validação do voo de volta
    if (idaVolta) {
      if (!values.dataVooVolta) {
        message.error("Informe a data do voo de volta.");
        return;
      }
      if (values.dataVoo && values.dataVooVolta.isBefore(values.dataVoo, "day")) {
        message.error("A data da volta não pode ser anterior à data da ida.");
        return;
      }
    }

    if (origemMilhas === "consolidadora") {
      if (!precoConsolidadora || precoConsolidadora <= 0) {
        message.error("Informe o preço pago à consolidadora.");
        return;
      }
    } else {
      if (!milhasUtil || milhasUtil <= 0) { message.error("Informe as milhas utilizadas."); return; }
      if (!cartaoSel)                    { message.error("Selecione a conta/programa."); return; }
    }

    setSaving(true);
    try {
      /*
       * CONTRATO COM O BACKEND DELPHI (PostInserirVendas):
       *
       * QtdeMilhas → StrToInt()  → NUNCA enviar "9.813.000" (explode)
       *                         → enviar "9813000"
       *
       * ValorUnitario → ConverterParaFloat() → backend faz: vPrecoMilheiro = vValorUnitario * 1000
       *                         → enviamos o valor POR MILHA (não por milheiro)
       *                         → ex: R$18/milheiro → enviar "0,018"
       *
       * Economia / Taxas / Bagagem → StrToCurr()
       *                         → Delphi locale pt-BR: decimal = VÍRGULA
       *                         → enviar "1234,56" (não "1234.56")
       *
       * Economia = precoPagante - (custoMilhas + taxas + bagagem)
       * PEconomia = economia / precoPagante * 100
       *
       * NomeConsolidadora / PrecoConsolidadora
       *                         → só relevantes quando origemMilhas === "consolidadora"
       *                         → o backend concatena esses dados em ven_observacao
       *                           para não perder o custo real da operação
       *                           (antes disso, ValorUnitario ia zerado nesse fluxo,
       *                           porque não existe cartão/custoMedio nessa origem)
       *
       * DataVooVolta → 🔥 NOVO
       *                         → STRING VAZIA "" quando for só-ida.
       *                         → NÃO enviar null/undefined: no Delphi,
       *                           Conteudo.Values['DataVooVolta'] com JSON null
       *                           devolve TJSONNull (que é <> nil) e o .Value
       *                           estouraria dentro do StrToDateTime.
       *                         → O backend testa Trim(...) <> '' e grava NULL
       *                           na coluna ven_data_voo_volta quando vazio.
       */
      const valorPorMilha = custoCalc > 0 ? custoCalc / 1000 : 0;
      const tipoOperacao  = TIPO_OPERACAO[origemMilhas] || "FRENTE VOO";

      const payload = {
        IDCliente:       clienteId,
        // Consolidadora não usa cartão do cliente → envia 0, o backend já
        // aceita esse fluxo como venda de terceiros sem cartão vinculado.
        IDCartao:        cartaoSel ? cartaoSel.car_id : 0,

        // ← CRÍTICO: StrToInt não aceita pontos de milhar
        QtdeMilhas:      toDelphiInt(milhasUtil),

        // ← backend faz *1000 → enviamos por milha COM PRECISÃO (NÃO toDelphiCurr!)
        //    toDelphiCurr aqui zeraria o valor: 0,018 → "0,02"; 0,004 → "0,00"
        ValorUnitario:   toDelphiFloat(valorPorMilha, 6),

        // ← tipo da operação conforme a origem das milhas (backend lê SEM guard de nil)
        TipoOperacao:    tipoOperacao,

        // ← preço da passagem pagante (referência de economia)
        ValorTotal:      toDelphiCurr(precoPagante),

        FormaPagamento:  origemMilhas,
        Observacoes:     values.observacoes || "",
        Status:          1,

        // ← Interline (Azul internacional) não conta CPF → envia 0 nesse caso
        QtdePassageiros: String(cpfsContabilizados),
        Interline:       interline ? "1" : "0",

        // ← todos com vírgula decimal para StrToCurr
        Economia:        toDelphiCurr(economiaGerada),
        PEconomia:       toDelphiCurr(economiaPct),
        Taxas:           toDelphiCurr(taxas),
        Bagagem:         toDelphiCurr(bagagem),

        Localizador:     values.localizador || "",
        Origem:          values.origem      || "",
        Destino:         values.destino     || "",
        OrigemMilhas:    origemMilhas,

        DataVoo:         values.dataVoo
          ? values.dataVoo.format(DELPHI_DT)
          : dayjs().add(1, "day").format(DELPHI_DT),

        // 🔥 NOVO: só-ida manda "" → backend grava NULL.
        IdaVolta:        idaVolta ? "1" : "0",
        DataVooVolta:    idaVolta && values.dataVooVolta
          ? values.dataVooVolta.format(DELPHI_DT)
          : "",

        // dados reais da consolidadora, para o backend não gravar
        // ValorUnitario zerado e perder o custo real dessa operação.
        // Fora do fluxo consolidadora, vão vazios/zerados e o backend ignora.
        NomeConsolidadora:  origemMilhas === "consolidadora" ? nomeConsolidadora : "",
        PrecoConsolidadora: origemMilhas === "consolidadora" ? toDelphiCurr(precoConsolidadora) : "0,00",
      };

      console.log("[InserirVendas] payload →", payload);

      await api.post("/ServerPrincipal/InserirVendas", payload);

      message.success("Emissão registrada com sucesso!");
      form.resetFields();
      setPrecoPagante(0); setMilhasUtil(0); setTaxas(0); setBagagem(0);
      setCartaoSel(null); setPassagSel([]); setFileList([]);
      setInterline(false);
      setIdaVolta(false);
      setOrigemMilhas("proprias");
      setNomeFornecedor(""); setContatoFornecedor(""); setCustoMilheiroBalcao(0);
      setNomeConsolidadora(""); setPrecoConsolidadora(0);
    } catch (e) {
      const errMsg = e?.response?.data || e?.message || "Erro ao registrar emissão";
      message.error(String(errMsg));
      console.error("[InserirVendas] ERRO →", e?.response?.status, e?.response?.data);
    } finally {
      setSaving(false);
    }
  };

  // Para Consolidadora não existe cartão nem milhas do cliente — a única
  // exigência real é ter o preço pago à consolidadora. Exigir
  // milhasUtil/cartaoSel aqui (campos que nem são exibidos nesse fluxo)
  // era o motivo do botão nunca habilitar para esse tipo de origem.
  const isFormValid = origemMilhas === "consolidadora"
    ? precoConsolidadora > 0
    : (milhasUtil > 0 && !!cartaoSel);
  const fmt = (v) => v > 0 ? v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";

  const s = {
    card:    { background: "#fff", borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
    header:  { display: "flex", alignItems: "center", gap: 8, padding: "16px 20px 14px", borderBottom: "1px solid #f0f0f0" },
    body:    { padding: "20px 24px 24px" },
    lbl:     { fontSize: 12, color: "#555", fontWeight: 500, marginBottom: 4, display: "block" },
    inp:     { width: "100%", borderRadius: 4, border: "1px solid #d9d9d9", background: "#fafafa", height: 36, fontSize: 14 },
    dis:     { width: "100%", borderRadius: 4, border: "1px solid #e8e8e8", background: "#f5f5f5", height: 36, fontSize: 14, color: "#888" },
    fi:      { marginBottom: 14 },
    divider: { borderTop: "1px dashed #e0e0e0", margin: "16px 0" },
    submitBtn: {
      width: "100%", height: 36,
      background: isFormValid ? "#1677ff" : "#bfbfbf",
      border: "none", borderRadius: 4, color: "#fff",
      fontSize: 13, fontWeight: 600, letterSpacing: "0.05em",
      cursor: isFormValid ? "pointer" : "not-allowed",
    },
  };

  const renderCamposOrigem = () => {
    if (origemMilhas === "balcao") return (
      <>
        <div style={s.fi}>
          <label style={s.lbl}>Nome do Fornecedor:</label>
          <Input size="middle" style={s.inp} placeholder="Nome do fornecedor"
            value={nomeFornecedor} onChange={(e) => setNomeFornecedor(e.target.value)} />
        </div>
        <Row gutter={10} style={{ marginBottom: 14 }}>
          <Col xs={14}>
            <label style={s.lbl}>Contato do Fornecedor:</label>
            <Input size="middle" style={s.inp} placeholder="(XX) XXXXX-XXXXX"
              value={contatoFornecedor} onChange={(e) => setContatoFornecedor(e.target.value)}
              suffix={<span style={{ color: "#bbb", fontSize: 13 }}>📞</span>} />
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 3 }}>Campo opcional.</div>
          </Col>
          <Col xs={10}>
            <label style={s.lbl}>Custo do Milheiro</label>
            <MoneyInput
              style={s.inp}
              value={custoMilheiroBalcao}
              onChange={setCustoMilheiroBalcao}
              suffix={<span style={{ color: "#bbb" }}>$</span>}
            />
          </Col>
        </Row>
        <div style={s.divider} />
      </>
    );

    if (origemMilhas === "consolidadora") return (
      <>
        <Row gutter={10} style={{ marginBottom: 14 }}>
          <Col xs={16}>
            <label style={s.lbl}>Nome da Consolidadora:</label>
            <Input size="middle" style={s.inp} placeholder="Nome da consolidadora"
              value={nomeConsolidadora} onChange={(e) => setNomeConsolidadora(e.target.value)} />
          </Col>
          <Col xs={8}>
            <label style={s.lbl}>Preço da Consolidadora</label>
            <MoneyInput
              style={s.inp}
              value={precoConsolidadora}
              onChange={setPrecoConsolidadora}
              suffix={<span style={{ color: "#bbb" }}>$</span>}
            />
          </Col>
        </Row>
        <div style={s.divider} />
      </>
    );

    return null;
  };

  return (
    <Spin spinning={loading}>
      <div style={s.card}>
        <div style={s.header}>
          <span style={{ fontSize: 16 }}>✈</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>Emissões para Uso Próprio</span>
        </div>

        <div style={s.body}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}
            initialValues={{ dataEmissao: dayjs(), objetivoEmissao: "uso_proprio" }}>
            <Row gutter={24}>

              {/* ══ COLUNA ESQUERDA ══ */}
              <Col xs={24} md={13} style={{ borderRight: "1px dashed #e0e0e0", paddingRight: 24 }}>

                <div style={s.fi}>
                  <label style={s.lbl}>Origem das Milhas:</label>
                  <Form.Item name="origemMilhas" noStyle initialValue="proprias">
                    <Select size="middle" style={{ width: 220 }} onChange={handleOrigemChange}>
                      <Option value="proprias">Milhas Próprias</Option>
                      <Option value="balcao">Balcão de Milhas</Option>
                      <Option value="consolidadora">Consolidadora</Option>
                    </Select>
                  </Form.Item>
                </div>

                {renderCamposOrigem()}

                {origemMilhas !== "consolidadora" && (
                  <Row gutter={10} style={{ marginBottom: 14 }}>
                    <Col xs={24} sm={10}>
                      <label style={s.lbl}>Titular/Conta da Operação</label>
                      <Form.Item name="cartaoId" noStyle rules={[{ required: true, message: "Selecione" }]}>
                        <Select size="middle" style={{ width: "100%" }} placeholder="Selecione o programa"
                          loading={loading} onChange={handleCartaoChange}>
                          {cartoes.map((c) => (
                            <Option key={c.car_id} value={c.car_id}>
                              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <LogoCircle nome={c.car_nome_programa} size={20} />
                                {(c.cli_nome || "").split(" ")[0]} - {c.car_nome_programa}
                              </span>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={8} sm={5}>
                      <label style={s.lbl}>Saldo disponível</label>
                      <Input size="middle" style={s.dis} readOnly
                        value={saldoDisp > 0 ? saldoDisp.toLocaleString("pt-BR") : ""}
                        placeholder="0" suffix={<span style={{ color: "#bbb" }}>#</span>} />
                    </Col>
                    <Col xs={8} sm={5}>
                      <label style={s.lbl}>Custo Médio</label>
                      <Input size="middle" style={s.dis} readOnly
                        value={custoMedio > 0 ? fmt(custoMedio) : ""}
                        placeholder="0,00" suffix={<span style={{ color: "#bbb" }}>$</span>} />
                    </Col>
                    <Col xs={8} sm={4}>
                      <label style={s.lbl}>CPFs Livres</label>
                      <Input size="middle" style={s.dis} readOnly
                        value={cartaoSel ? cpfsLivres : ""} placeholder="0"
                        suffix={<span style={{ color: "#bbb" }}>#</span>} />
                    </Col>
                  </Row>
                )}

                {/* Consolidadora = compra de passagem já emitida por terceiros.
                    Não usa cartão/conta/milhas do cliente, então NÃO existe
                    "Titular/Conta da Operação" nesse fluxo — não renderiza nada aqui. */}

                {/* Interline: só aparece para programas Azul. Não consome cota de CPF. */}
                {isAzul && (
                  <div style={{ marginBottom: 14, marginTop: 4, display: "flex", alignItems: "center" }}>
                    <Checkbox checked={interline} onChange={(e) => setInterline(e.target.checked)}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>
                        Interline (emissões internacionais)
                      </span>
                    </Checkbox>
                    <Tooltip title="Emissões Interline (parceiras internacionais da Azul) não consomem a cota de CPF do programa. Ao marcar, o registro de passageiros deixa de ser necessário.">
                      <QuestionCircleOutlined style={{ color: "#bbb", marginLeft: 6, cursor: "help" }} />
                    </Tooltip>
                  </div>
                )}

                <div style={s.divider} />

                <Row gutter={10} style={{ marginBottom: 4 }}>
                  <Col xs={14}>
                    <label style={s.lbl}>Preço da Passagem Pagante</label>
                    <MoneyInput
                      style={s.inp}
                      value={precoPagante}
                      onChange={setPrecoPagante}
                      suffix={<span style={{ color: "#bbb" }}>$</span>}
                    />
                  </Col>
                  <Col xs={10}>
                    <label style={s.lbl}>&nbsp;</label>
                    <Upload fileList={fileList}
                      beforeUpload={(file) => {
                        const fd = new FormData();
                        fd.append("file", file);
                        api.post("/upload/images", fd, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => {});
                        setFileList([file]);
                        return false;
                      }}
                      maxCount={1} showUploadList={false} accept="image/*,.pdf">
                      <Button icon={<PaperClipOutlined />}
                        style={{ width: "100%", height: 36, border: "1px solid #d9d9d9", borderRadius: 4, background: "#fafafa", fontSize: 13 }}>
                        PRINT VÔO
                      </Button>
                    </Upload>
                    <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                      {fileList.length > 0 ? fileList[0].name : "Nenhum arquivo selecionado."}
                    </div>
                  </Col>
                </Row>

                {origemMilhas !== "consolidadora" && (
                  <div style={s.fi}>
                    <label style={s.lbl}>Milhas Utilizadas</label>
                    <InputNumber size="middle" style={s.inp} min={0} controls={false}
                      onChange={(v) => setMilhasUtil(Number(v) || 0)}
                      formatter={fmtMilhas}
                      parser={parseMilhas}
                      suffix={<span style={{ color: "#bbb" }}>#</span>} />
                  </div>
                )}

                {origemMilhas !== "consolidadora" && (
                  <div style={s.fi}>
                    <label style={s.lbl}>Preço em Milhas</label>
                    <Input size="middle" style={s.dis} readOnly value={fmt(precoEmMilhas)}
                      placeholder="0,00" suffix={<span style={{ color: "#bbb" }}>$</span>} />
                  </div>
                )}

                <Row gutter={10} style={{ marginBottom: 14 }}>
                  <Col xs={12}>
                    <label style={s.lbl}>Taxas</label>
                    <MoneyInput
                      style={s.inp}
                      value={taxas}
                      onChange={setTaxas}
                      suffix={<span style={{ color: "#bbb" }}>$</span>}
                    />
                  </Col>
                  <Col xs={12}>
                    <label style={s.lbl}>Bagagem/Extras</label>
                    <MoneyInput
                      style={s.inp}
                      value={bagagem}
                      onChange={setBagagem}
                      suffix={<span style={{ color: "#bbb" }}>$</span>}
                    />
                  </Col>
                </Row>

                <Row gutter={10} style={{ marginBottom: 14 }}>
                  <Col xs={12}>
                    <label style={s.lbl}>Economia Gerada</label>
                    <Input size="middle" style={s.dis} readOnly value={fmt(economiaGerada)}
                      placeholder="0,00" suffix={<span style={{ color: "#bbb" }}>$</span>} />
                  </Col>
                  <Col xs={12}>
                    <label style={s.lbl}>Economia em %</label>
                    <Input size="middle" style={s.dis} readOnly
                      value={economiaPct > 0 ? fmt(economiaPct) : ""}
                      placeholder="0" suffix={<span style={{ color: "#bbb" }}>%</span>} />
                  </Col>
                </Row>

                <Row gutter={10} style={{ marginBottom: 14 }}>
                  <Col xs={12}>
                    <label style={s.lbl}>Localizador do Vôo</label>
                    <Form.Item name="localizador" noStyle rules={[{ required: true, message: "Campo obrigatório." }]}>
                      <Input size="middle" style={s.inp} />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <label style={s.lbl}>Data do Vôo</label>
                    <Form.Item name="dataVoo" noStyle>
                      <DatePicker style={s.inp} format="DD/MM/YYYY" size="middle" />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── 🔥 NOVO: VOO DE IDA E VOLTA ────────────────────────────
                    Marcado → exibe o DatePicker de retorno (obrigatório) e o
                    payload leva DataVooVolta preenchida.
                    Desmarcado → DataVooVolta vai como "" e o backend grava
                    NULL em ven_data_voo_volta (antes gravava Now+1 falso). */}
                <div style={{ marginBottom: 14 }}>
                  <Checkbox
                    checked={idaVolta}
                    onChange={(e) => handleIdaVoltaChange(e.target.checked)}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#333" }}>
                      Voo de ida e volta
                    </span>
                  </Checkbox>
                  <Tooltip title="Marque se a emissão inclui o trecho de retorno. A data da volta é gravada no campo ven_data_voo_volta.">
                    <QuestionCircleOutlined style={{ color: "#bbb", marginLeft: 6, cursor: "help" }} />
                  </Tooltip>

                  {idaVolta && (
                    <div style={{ marginTop: 10 }}>
                      <label style={s.lbl}>Data do Vôo de Volta</label>
                      <Form.Item
                        name="dataVooVolta"
                        noStyle
                        rules={[{ required: true, message: "Informe a data da volta." }]}
                      >
                        <DatePicker
                          style={{ ...s.inp, maxWidth: 240 }}
                          format="DD/MM/YYYY"
                          size="middle"
                          placeholder="Selecione a data"
                          // A volta nunca pode ser anterior à ida.
                          disabledDate={(c) => {
                            const ida = form.getFieldValue("dataVoo");
                            return !!c && !!ida && c < ida.startOf("day");
                          }}
                        />
                      </Form.Item>
                    </div>
                  )}
                </div>

                <Row gutter={10} style={{ marginBottom: 14 }}>
                  <Col xs={12}>
                    <label style={s.lbl}>Origem do Vôo</label>
                    <Form.Item name="origem" noStyle>
                      <Input size="middle" style={s.inp} />
                    </Form.Item>
                  </Col>
                  <Col xs={12}>
                    <label style={s.lbl}>Destino do Vôo</label>
                    <Form.Item name="destino" noStyle>
                      <Input size="middle" style={s.inp} />
                    </Form.Item>
                  </Col>
                </Row>

                <div style={s.fi}>
                  <label style={s.lbl}>Data da Emissão</label>
                  <Form.Item name="dataEmissao" noStyle>
                    <DatePicker style={s.inp} format="DD/MM/YYYY" size="middle"
                      disabledDate={(c) => c && c > dayjs().endOf("day")} />
                  </Form.Item>
                </div>

                <div style={s.fi}>
                  <label style={s.lbl}>Objetivo da Emissão</label>
                  <Form.Item name="objetivoEmissao" noStyle>
                    <Select size="middle" style={{ width: "100%" }}>
                      <Option value="uso_proprio">Uso Próprio</Option>
                      <Option value="presente">Presente</Option>
                      <Option value="venda">Venda</Option>
                      <Option value="outro">Outro</Option>
                    </Select>
                  </Form.Item>
                </div>

                <div style={s.fi}>
                  <label style={s.lbl}>Observações:</label>
                  <Form.Item name="observacoes" noStyle>
                    <Input.TextArea rows={4}
                      style={{ width: "100%", borderRadius: 4, border: "1px solid #d9d9d9", fontSize: 13, resize: "vertical" }} />
                  </Form.Item>
                </div>
              </Col>

              {/* ══ COLUNA DIREITA: Passageiros ══ */}
              <Col xs={24} md={11} style={{ paddingLeft: 24 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>Registro de Passageiros</span>
                {semControleCpf ? (
                  <p style={{ fontSize: 12, color: "#1677ff", marginTop: 4, marginBottom: 12 }}>
                    {origemMilhas === "consolidadora"
                      ? "Venda de passagem de terceiros — não há controle de CPF nesta operação."
                      : "Esta emissão usou o Interline (não conta CPFs)."}
                  </p>
                ) : (
                  <p style={{ fontSize: 12, color: "#888", marginTop: 4, marginBottom: 12 }}>
                    Você pode registrar até {cpfsLivres} passageiros nesta operação.
                  </p>
                )}

                <div style={{ position: "relative", marginBottom: 12 }}>
                  <Input size="middle" placeholder="Digite algum dado do passageiro"
                    value={buscaPassag} onChange={(e) => setBuscaPassag(e.target.value)}
                    suffix={<SearchOutlined style={{ color: "#bbb" }} />} style={{ borderRadius: 4 }} />
                  {buscaPassag.length > 0 && (
                    <div style={{ position: "absolute", top: 38, left: 0, right: 0, zIndex: 100, background: "#fff", borderRadius: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.14)", maxHeight: 200, overflowY: "auto" }}>
                      {passageirosFiltrados.length === 0
                        ? <div style={{ padding: "10px 14px", fontSize: 13, color: "#999" }}>Nenhum passageiro encontrado</div>
                        : passageirosFiltrados.map((p) => (
                          <div key={p.pag_id}
                            style={{ padding: "10px 14px", fontSize: 13, cursor: "pointer", borderBottom: "1px solid #f5f5f5" }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#f5f5f5"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "none"}
                            onClick={() => adicionarPassageiro(p)}
                          >{p.pag_nome}</div>
                        ))
                      }
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 11, fontWeight: 600, color: "#888", padding: "4px 0 8px", borderBottom: "1px solid #f0f0f0", marginBottom: 8 }}>Nome</div>

                <div style={{ maxHeight: 280, overflowY: "auto" }}>
                  {passagSel.length === 0
                    ? <div style={{ padding: "16px 12px", background: "#fafafa", borderRadius: 4, border: "1px solid #f0f0f0", color: "#aaa", fontSize: 13, textAlign: "center" }}>
                        Utilize o campo acima para adicionar passageiros ao vôo.
                      </div>
                    : passagSel.map((p) => (
                      <div key={p.pag_id}
                        style={{ padding: "8px 10px", marginBottom: 6, borderRadius: 4, background: "#f9f9f9", border: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#222" }}>{p.pag_nome}</div>
                          {p._isTitular
                            ? <div style={{ fontSize: 11, color: "#1677ff", marginTop: 2 }}>Este passageiro é o titular da conta.</div>
                            : <div style={{ fontSize: 11, color: "#52c41a", marginTop: 2 }}>CPF contabilizado nesta emissão.</div>
                          }
                        </div>
                        <button style={{ background: "none", border: "none", cursor: "pointer", color: "#bbb", padding: "0 4px", fontSize: 14 }}
                          onClick={() => removerPassageiro(p.pag_id)}>×</button>
                      </div>
                    ))
                  }
                </div>

                {!semControleCpf && passagSel.length === 0 && (
                  <div style={{ marginTop: 8, color: "#ff4d4f", fontSize: 12 }}>Inclua pelo menos um passageiro.</div>
                )}

                {/* Consolidadora não tem cartão/cota de CPF associada — esse
                    resumo só faz sentido quando existe um programa selecionado. */}
                {origemMilhas !== "consolidadora" && (
                  <div style={{ marginTop: 16, padding: "10px 12px", background: "#f9f9f9", borderRadius: 4, border: "1px solid #f0f0f0" }}>
                    <div style={{ fontSize: 12, color: "#555" }}>CPFs limitados pelo programa: <strong>{cpfsLivres}</strong></div>
                    <div style={{ fontSize: 12, color: "#555" }}>
                      CPFs desta emissão: <strong>{cpfsContabilizados}</strong>
                      {interline && <span style={{ color: "#1677ff" }}> (Interline não consome CPF)</span>}
                    </div>
                  </div>
                )}
              </Col>
            </Row>

            <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
              <button type="submit" style={s.submitBtn} disabled={saving || !isFormValid}>
                {saving ? "PROCESSANDO..." : isFormValid ? "CONFIRMAR EMISSÃO" : "PREENCHA OS DADOS!"}
              </button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </Spin>
  );
}